import React, {useContext, useEffect, useState, useCallback} from "react";
import evaluateOutputCalculation from "../utils/evaluateOutputCalculation";
import ConfiguratorContext from "./ConfiguratorContext";
import ConfiguratorSurveyOutputRender from "./ConfiguratorSurveyOutputRender";
import ConfiguratorSurveyRenderResult from "./ConfiguratorSurveyRenderResult";
import {FormattedMessage, useIntl} from "react-intl";
import Button from "../ui-components/Button";
import {
  ArrowPathIcon, CalculatorIcon,
  ClockIcon,
  ExclamationCircleIcon
} from "@heroicons/react/24/outline";
import ConfiguratorSurveyOutputKCC from "@components/ConfiguratorSurveyOutputKCC";
import moment from "moment/moment";
import {useKccResults} from "../feature/surveys/hooks/useKccResults";
import retrieveKCCOptimizationResponses from "@utils/retrieveKCCOptimizationResponses";
import getKccResultContextKey from "@utils/getKccResultContextKey";
import {extractConnectorResponses} from "../feature/surveys/lib/connectors/utils";
import {useConnectorsResults} from "../feature/surveys/hooks/useConnectorsResults";
import {CheckCircleIcon} from "@heroicons/react/24/solid";
import {useSpreadsheetResults} from "../feature/surveys/hooks/useSpreadsheetResults";
import {useComputeKColor} from "@feature/kcolor/hooks/useComputeKColor";
import _ from "lodash";
import {NotificationsContext} from "@ui-components/Notifications";
import ConfiguratorSurveyKColorOutput from "./ConfiguratorSurveyKColorOutput";
import getKColorResultContextKey from "@utils/getKColorResultContextKey";
import TimbertechWoodMaterialsCSV from "feature/services/connettori/assets/timbertech-wood-materials.csv";
import TimbertechCACSV from "feature/services/connettori/assets/timbertech-ca.csv";
import {useCSV} from "@feature/commons/hooks/useCSV";
import {storeConfigurationKpi} from "../utils/storeConfigurationKpi";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import useGetOptionTitle from "../hooks/useGetOptionTitle";
import retrieveKColorResponses from "@utils/retrieveKColorResponses";


const ConfiguratorSurveyOutputCalculation = ({
  output,
  role,
  realtime,
  responses,
  summary,
  ...rest
}) => {

  // contexts
  const context = useContext(ConfiguratorContext);
  const {push} = useContext(NotificationsContext);
  const countryApiRequest = useCountryAxiosInstance();
  const getOptionTitle = useGetOptionTitle();

  // hooks
  const intl = useIntl();
  const {recompute: recomputeKcc, loading: kccLoading} = useKccResults();
  const {
    recompute: retrieveConnectorResults,
    override: overrideConnectorResult,
    download: downloadConnectorResults,
    loading: connectorsLoading,
    downloading: connectorsDownloading,
  } = useConnectorsResults();
  const timbertechWoodMaterials = useCSV(TimbertechWoodMaterialsCSV);
  const timbertechCA = useCSV(TimbertechCACSV);

  const {
    recompute: recomputeSpreadsheet,
    loading: spreadsheetLoading,
  } = useSpreadsheetResults();

  const {
    recompute: recomputeKColor,
    loading: kColorLoading,
  } = useComputeKColor(false);


  // state
  const [asyncResults, setAsyncResults] = useState();

  // utilities - sync/async computation
  const getCalculationProperty = (prop, default_=undefined) => {
    if (
      output?.calculation?.length !== 1
      || !output.calculation[0].startsWith('{')
    )
      return default_;
    return JSON.parse(output.calculation[0])[prop] || default_;
  }
  const isCustomCalculation = (type) => getCalculationProperty("original") === type;
  const isKCCOptimization = isCustomCalculation('kcc_optimization');
  const isConnectors = isCustomCalculation('connector');
  const isSpreadsheet = isCustomCalculation('spreadsheet');
  const isRecursiveSum = isCustomCalculation('recursive_sum');
  const isKColor = isCustomCalculation('kcolor');
  const isSyncCalculation = !isKCCOptimization && !isConnectors && !isSpreadsheet && !isKColor;

  /*
  if it's a kcolor output, gets the kcolor input value
  checking in the responses or in the context.responses the
  corresponding kcolor input value!!
   */
  const getKColorValue = useCallback(() => {
    if (!isKColor) return;

    const inputId = JSON.parse(output.calculation[0])?.kcolor_question;
    return (responses || context.responses)[inputId]?.value;
  }, [responses, context.responses, output.calculation]);

  const getSurfaceValue = useCallback(() => {
    if (!isKColor) return;

    const inputId = JSON.parse(output.calculation[0])?.surface_question;
    return (responses || context.responses)[inputId]?.value;
  }, [responses, context.responses, output.calculation]);

  const getDisableComputeButton = () => {
    if (isSpreadsheet) {
      return context.isCalculatingSpreadsheetsResults
    }
    if (isKColor){
      const value = getKColorValue();
      return (
        // as in a qutotation we are using remote calculation results of this survey in order to retrieve kcolor prices
        // list price and tintometer prices are not editable via the recompute button!!!!! they may change and affect
        // the quotation integrity
        !value || !value.color || !value.kColorProduct || !value.productPackage || (context.survey !== undefined && context.survey.not_editable)
      )
    }
  }

  // utilities - async data (re)computation
  const retrieveConnectorResponses = () => {
    let connectorResponses;
    try {
      const type = getCalculationProperty("connector_type", "diaphragm");
      if (type === "wood" && (!timbertechWoodMaterials || !timbertechCA))
        return;
      const files = type === "wood"
        ? {timbertechWoodMaterials, timbertechCA}
        : undefined;
      connectorResponses = extractConnectorResponses(
        output,
        responses || context.responses,
        files,
      );
    }
    catch (e) {
      push({
        type: "error",
        title: intl.formatMessage({
          id: "connectors_error_responses_extraction",
          defaultMessage: "Errore durante il recupero delle risposte necessarie al calcolo",
        }),
      })
      console.error(e);
    }
    return connectorResponses;
  }
  const onAsyncCompute = async () => {
    if (isKCCOptimization) {
      // update the kcc context with the latest response
      const kccResponses = retrieveKCCOptimizationResponses(output, responses, context);
      const kccContextKey = getKccResultContextKey(kccResponses);
      const results = await recomputeKcc(
        kccResponses,
        context?.service?.type === 'tintometer',
        context.survey?.id,
      );
      context.setKccOptimizationResults((prev) => ({
        ...prev,
        [kccContextKey]: results,
      }));
      await storeConfigurationKpi(
        {
          ...context,
          kccOptimizationResults: {
            ...context.kccOptimizationResults,
            [kccContextKey]: results,
          },
        },
        getOptionTitle,
        countryApiRequest
      );
    }
    else if (isConnectors) {

      // Extract the responses needed to compute the connectors
      const connectorResponses = retrieveConnectorResponses();
      if (connectorResponses === undefined)
        return;

      // Call the remote API to compute the connectors and update the context
      const type = getCalculationProperty("connector_type", "diaphragm");
      const results = await retrieveConnectorResults(
        connectorResponses,
        context.survey?.id,
        type,
      );
      context.setConnectionResults(results);
    }
    else if (isSpreadsheet){
      context.setIsCalculatingSpreadsheetsResults(true)
      const resps = _.cloneDeep(responses || context.responses)
      for (const [id, response] of Object.entries(resps)) {
        if (response.recursive_question_value?.length > 0) {
          response.recursive_question_value = response.recursive_question_value
            .filter(item => !_.isEmpty(item))
        }
      }
      context.setResponses(resps)
      const spreadsheetValues = await recomputeSpreadsheet(responses || context.responses, context.survey?.id, output, context.spreadsheetResults);
      context.setSpreadsheetResults({...context.spreadsheetResults, ...spreadsheetValues});
      context.setIsCalculatingSpreadsheetsResults(false)
      await storeConfigurationKpi(
        {
          ...context,
          spreadsheetResults: {...context.spreadsheetResults, ...spreadsheetValues},
        },
        getOptionTitle,
        countryApiRequest
      );
    }
    else if (isKColor) {

      let kColor = getKColorValue();
      if (!kColor)
        return;

      context.setIsCalculatingKColorResults(true);
      try {

        // Retrieve result from API
        let result;
        try {
          result = await recomputeKColor(kColor, context.survey?.id);
        } catch (e) {
          if (e.response?.status === 404) {
            push({
              type: "warning",
              title: intl.formatMessage({
                id: "kcolor_price_list_not_found",
                defaultMessage: "Listino non trovato per la combinazione selezionata",
              }),
            });
          } else {
            console.error(e);
            push({
              type: "error",
              title: intl.formatMessage({id: "server_error"}),
            });
          }
          return;  // Exit here, don't update the context
        }

        // Update context with the retrieved result
        const key = getKColorResultContextKey(
          kColor.kColorProduct.cod_prod_comm,
          kColor.color?.code,
          kColor.productPackage,
          rest.recursiveResponsesIndex,
        );
        if (!key)
          throw Error("invalid key!");
        context.setKColorResults({
          ...context.kColorResults,
          [key]: {...result, updated_at: new Date().toISOString()},
        });

      }
      finally{
        context.setIsCalculatingKColorResults(false);
      }
    }
    else
      throw Error("Trying to asynchronously compute a non-async output");
  }
  // utilities - generic
  const isSlim = output.output_thickness <= 1;
  const textTitle = realtime
    ? (isSlim ? "text-sm" : "text-lg")
    : (isSlim ? "text-xl" : "text-3xl font-bold");
  const isPartialTotal =
    isKCCOptimization
    && asyncResults?.value.some((row) => !row.entry_unit_price);
  const ComputationIcon =
    !!asyncResults ? ArrowPathIcon : CalculatorIcon;

  // utilities - output value computation
  const result = evaluateOutputCalculation(output, {
      ...context,
      responses: responses || context.responses,
    },
    undefined,
    rest.recursiveResponsesIndex);

  // lifecycle
  // whenever the kcc results inside the context change, or this output changes, update the local async data
  useEffect(() => {
    if (!isKCCOptimization || !context)
      return;

    const kccResponses = retrieveKCCOptimizationResponses(output, responses, context);
    const kccContextKey = getKccResultContextKey(kccResponses);
    const data = context.kccOptimizationResults?.[kccContextKey];

    if (data)
      setAsyncResults(data);
  }, [context?.kccOptimizationResults, output]);
  // whenever the connector results inside the context change, or this output changes, update the local async data
  useEffect(() => {
    if (!isConnectors || !context?.connectionResults)
      return;
    const type = getCalculationProperty("connector_type", "diaphragm");
    setAsyncResults(context.connectionResults[type]);
  }, [context?.connectionResults, output])
  // whenever the spreadsheet results inside the context change, or this output changes, update the local async data
  useEffect(() => {
    if (!isSpreadsheet || !context || _.isEmpty(context.spreadsheetResults?.[output.id]))
      return;
    setAsyncResults(context.spreadsheetResults);
    context.setSpreadsheetResults(context.spreadsheetResults)
  }, [context?.spreadsheetResults, output])
  // whenever the kcolor results inside the context change, or this output changes, update the local async data
  useEffect(() => {
    if (!isKColor || !context)
      return;
    const {product, color, productPackage} = retrieveKColorResponses(context.responses, output, rest.recursiveResponsesIndex);
    const key = getKColorResultContextKey(product, color, productPackage);
    setAsyncResults(context?.kColorResults?.[key]);
  }, [context?.kColorResults, output])

  return <>
    <ConfiguratorSurveyOutputRender {...output} role={role} realtime={realtime}>
      <div>
        <h2 className={
          "flex gap-2 items-center "
          + (isPartialTotal ? (summary ? 'text-yellow-500' : 'text-yellow-600') : '')
          + ` ${textTitle}`
        }>
          {isPartialTotal && <ExclamationCircleIcon className={summary ? 'w-10' : 'w-6'} />}
          <ConfiguratorSurveyRenderResult
            result={
              isSyncCalculation
                ? result
                : (isConnectors || isKColor
                  ? undefined
                  : (
                    !!asyncResults
                      ? result
                      : null
                  )
                )
            }
            currency={output.output_currency}
            realtime={realtime}
          />
        </h2>
      </div>
    </ConfiguratorSurveyOutputRender>
    {!isSyncCalculation && (
      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-3 my-3">
          <Button
            onClick={onAsyncCompute}
            submitting={kccLoading || connectorsLoading || spreadsheetLoading || kColorLoading}
            disabled={context.isCalculatingSpreadsheetsResults || getDisableComputeButton()}
          >
            <ComputationIcon className={summary ? 'w-5' : 'w-4'}/>
            <span className="text-base">
            <FormattedMessage id={!asyncResults ? "compute" : "recompute"}/>
          </span>
          </Button>
          {!!asyncResults?.updated_at && (
            <div className="flex items-center gap-1">
              <ClockIcon className={summary ? 'w-4' : 'w-3'}/>
              <span className={summary ? 'text-base' : 'text-xs'}>
              <FormattedMessage id="last_update"/>:&nbsp;
                {moment(asyncResults.updated_at).format("DD/MM/YYYY")}
            </span>
            </div>
          )}
          {isConnectors && !connectorsLoading && !!asyncResults && (
            <div className="flex flex-row items-center gap-2">
              <CheckCircleIcon className={`w-5 flex-shrink-0 ${realtime ? "" : "text-green-500"}`}/>
              <span>
              <FormattedMessage id="data_computed_successfully"/>
                {" "}
                <span
                  className={`underline lowercase ${connectorsDownloading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={async () => {
                    if (connectorsDownloading)
                      return;
                    const connectorResponses = retrieveConnectorResponses();
                    if (!connectorResponses)
                      return;
                    const type = getCalculationProperty("connector_type", "diaphragm");
                    await downloadConnectorResults(connectorResponses, asyncResults, type, false, context.survey.name);
                  }}
                >
                <FormattedMessage
                  id="download_report"
                  defaultMessage="Scarica report"
                  className="lowercase"
                />
              </span>
            </span>
              {connectorsDownloading && (
                <ArrowPathIcon className="w-5 animate-spin text-gray-500"/>
              )}
            </div>
          )}
        </div>
      </div>
    )}
    {isKCCOptimization && (
      <ConfiguratorSurveyOutputKCC
        results={asyncResults}
        loading={kccLoading}
        summary={summary}
      />
    )}
    {isKColor && (
      <ConfiguratorSurveyKColorOutput
        context={context}
        output={output}
        recursiveResponsesIndex={rest.recursiveResponsesIndex}
      />
    )}
  </>
};

export default ConfiguratorSurveyOutputCalculation;
